const getDivWithContent = (content: string): HTMLDivElement => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  return tempDiv;
};

export interface HtmlImageData {
  alt: string;
  src: string;
}

/**
 * Extracts image metadata from HTML in document order.
 *
 * @example
 * extractImageTagsFromHtml('<img src="https://example.com/a.png" alt="A" /><p>Body</p>')
 * // => [{ src: 'https://example.com/a.png', alt: 'A' }]
 */
export function extractImageTagsFromHtml(html?: string | null): HtmlImageData[] {
  if (!html) {
    return [];
  }

  return Array.from(getDivWithContent(html).querySelectorAll('img'))
    .map((imageElement) => ({
      alt: imageElement.getAttribute('alt')?.trim() ?? '',
      src: imageElement.getAttribute('src')?.trim() ?? '',
    }))
    .filter((image) => image.src.length > 0);
}

/**
 * Removes all image tags while preserving the rest of the markup.
 *
 * @example
 * removeImageTagsFromHtml('<div><img src="x" /><p>test</p></div>')
 * // => '<div><p>test</p></div>'
 */
export function removeImageTagsFromHtml(html?: string | null): string {
  if (!html) {
    return '';
  }

  const contentDiv = getDivWithContent(html);
  contentDiv.querySelectorAll('img').forEach((imageElement) => imageElement.remove());
  return contentDiv.innerHTML;
}

export function getRawTextFromHTML(html: string): string {
  if (!html) return '';

  return getDivWithContent(html).textContent ?? '';
}

export function getDisplayedTextFromHtml(html?: string | null): string {
  if (!html) {
    return '';
  }

  const contentDiv = getDivWithContent(html);
  const isBlockElement = (tagName: string): boolean => {
    return [
      'BLOCKQUOTE',
      'DIV',
      'H1',
      'H2',
      'H3',
      'H4',
      'H5',
      'H6',
      'LI',
      'P',
      'PRE',
    ].includes(tagName);
  };

  const normalizeInlineWhitespace = (value: string): string => {
    return value
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n');
  };

  const walkNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return normalizeInlineWhitespace(node.textContent ?? '');
    }

    if (!(node instanceof HTMLElement)) {
      return '';
    }

    if (node.tagName === 'BR') {
      return '\n';
    }

    if (node.tagName === 'IMG' || node.tagName === 'SCRIPT' || node.tagName === 'STYLE') {
      return '';
    }

    const content = Array.from(node.childNodes)
      .map((childNode) => {
        return walkNode(childNode);
      })
      .join('');

    if (!isBlockElement(node.tagName)) {
      return content;
    }

    if (content.length === 0) {
      return '\n';
    }

    return `${content.replace(/\n+$/g, '')}\n`;
  };

  return Array.from(contentDiv.childNodes)
    .map((childNode) => {
      return walkNode(childNode);
    })
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();
}

export function decodeHtmlEntities(value?: string | null): string {
  if (!value) {
    return '';
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;

  return textarea.value;
}

export function hasImgTags(html: string): boolean {
  return getDivWithContent(html).querySelector('img') !== null;
}

export function htmlToPlainText(html?: string | null): string {
  // keep links text, collapse whitespace
  const withoutTags = (html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ') // paragraph break -> space
    .replace(/<[^>]+>/g, ''); // strip remaining tags
  return withoutTags.replace(/\s+/g, ' ').trim();
}
